import { motion } from 'framer-motion';

interface Option {
  label: string;
  value: number;
  tag?: string;
}

interface QuestionCardProps {
  question: string;
  subtitle?: string;
  options: Option[];
  selected: number | null;
  onSelect: (value: number) => void;
  questionNumber: number;
  totalQuestions: number;
}

const QuestionCard = ({
  question,
  subtitle,
  options,
  selected,
  onSelect,
  questionNumber,
  totalQuestions,
}: QuestionCardProps) => {
  return (
    <motion.div
      key={questionNumber}
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      transition={{ duration: 0.4 }}
      className="w-full"
    >
      <div className="mb-2 flex items-center gap-3">
        <span className="text-gold text-sm font-semibold tracking-widest uppercase">
          Question {questionNumber} of {totalQuestions}
        </span>
      </div>

      <div className="w-full bg-white/10 rounded-full h-1.5 mb-8">
        <motion.div
          className="bg-gold h-1.5 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${((questionNumber - 1) / totalQuestions) * 100}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <h3 className="text-2xl md:text-3xl font-black text-white mb-2 leading-tight font-serif">
        {question}
      </h3>
      {subtitle && (
        <p className="text-cream/60 text-sm mb-8">{subtitle}</p>
      )}

      <div className="grid gap-3 mt-8">
        {options.map((option, i) => (
          <motion.button
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            onClick={() => onSelect(option.value)}
            className={`w-full text-left px-6 py-4 rounded-xl border-2 transition-all duration-200 cursor-pointer flex items-center justify-between group whitespace-normal ${
              selected === option.value
                ? 'border-gold bg-gold/20 text-white'
                : 'border-white/10 bg-white/5 text-cream/80 hover:border-gold/50 hover:bg-white/10'
            }`}
          >
            <span className="text-base font-medium">{option.label}</span>
            <div className="flex items-center gap-2 shrink-0 ml-4">
              {option.tag && (
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  option.value >= 8
                    ? 'bg-red-500/30 text-red-300'
                    : option.value >= 5
                    ? 'bg-amber-500/30 text-amber-300'
                    : 'bg-green-500/20 text-green-300'
                }`}>
                  {option.tag}
                </span>
              )}
              <div className={`w-5 h-5 flex items-center justify-center rounded-full border-2 transition-all ${
                selected === option.value ? 'border-gold bg-gold' : 'border-white/30'
              }`}>
                {selected === option.value && (
                  <i className="ri-check-line text-black text-xs"></i>
                )}
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
};

export default QuestionCard;
